import React, { useState, useEffect } from 'react';
import { 
  Users, UserPlus, Shield, Building, Trash2, Edit3, 
  Search, X, Check, Clock, AlertCircle, Loader2,
  LayoutDashboard, Layers, UserCircle, Building2, FileText, BarChart3, Settings
} from 'lucide-react';
import { getUsuarios, getClientes, crearUsuario, actualizarUsuario, eliminarUsuario } from '../api';

const UserManagement = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  
  // Form State
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'cliente',
    clienteId: '',
    permisos: ['dashboard', 'procesos']
  });

  const availableModules = [
    { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
    { id: 'procesos', name: 'Procesos', icon: Layers },
    { id: 'candidatos', name: 'Candidatos', icon: UserCircle },
    { id: 'clientes', name: 'Clientes', icon: Building2 },
    { id: 'cotizaciones', name: 'Cotizaciones', icon: FileText },
    { id: 'reportes', name: 'Reportes', icon: BarChart3 },
    { id: 'usuarios', name: 'Gestión Usuarios', icon: Settings },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [uRes, cRes] = await Promise.all([getUsuarios(), getClientes()]);
      setUsuarios(uRes.data);
      setClientes(cRes.data);
    } catch (err) {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        nombre: user.nombre,
        email: user.email,
        password: '', // Password field blank when editing
        rol: user.rol,
        clienteId: user.clienteId || '',
        permisos: user.permisos || []
      });
    } else {
      setEditingUser(null);
      setFormData({
        nombre: '',
        email: '',
        password: '',
        rol: 'cliente',
        clienteId: '',
        permisos: ['dashboard', 'procesos']
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      if (editingUser) {
        // Enviar solo el password si no está vacío
        const dataToUpdate = { ...formData };
        if (!dataToUpdate.password) delete dataToUpdate.password;
        await actualizarUsuario(editingUser.id, dataToUpdate);
      } else {
        await crearUsuario(formData);
      }
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al procesar usuario');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario?')) {
      try {
        await eliminarUsuario(id);
        fetchData();
      } catch (err) {
        alert('Error al eliminar usuario');
      }
    }
  };

  const togglePermission = (moduleId) => {
    setFormData(prev => ({
      ...prev,
      permisos: prev.permisos.includes(moduleId)
        ? prev.permisos.filter(id => id !== moduleId)
        : [...prev.permisos, moduleId]
    }));
  };

  const filteredUsuarios = usuarios.filter(u => 
    u.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.Cliente?.nombre || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return (
    <div className="flex h-full items-center justify-center p-20">
      <Loader2 className="w-10 h-10 animate-spin text-[#2C4F1A]" />
    </div>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-800 flex items-center gap-3 tracking-tight">
            <Shield className="text-[#2C4F1A]" /> Gestión de Usuarios
          </h1>
          <p className="text-gray-500 mt-1">Administra los accesos y permisos dinámicos de la plataforma.</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="bg-[#2C4F1A] text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2 shadow-lg shadow-green-900/20 hover:bg-[#1E3812] transition-all transform active:scale-95"
        >
          <UserPlus size={18} /> Nuevo Usuario
        </button>
      </div>

      {/* Stats/Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
            <Users size={24} />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-400 uppercase tracking-wider">Total Usuarios</p>
            <p className="text-2xl font-black text-gray-800">{usuarios.length}</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center">
            <Check size={24} />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-400 uppercase tracking-wider">Super Admins</p>
            <p className="text-2xl font-black text-gray-800">{usuarios.filter(u => u.rol === 'superadmin').length}</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center">
            <Building size={24} />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-400 uppercase tracking-wider">Clientes Activos</p>
            <p className="text-2xl font-black text-gray-800">{clientes.length}</p>
          </div>
        </div>
      </div>

      {/* Search and Table */}
      <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-50 flex flex-col md:flex-row gap-4 justify-between items-center">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
            <input 
              type="text" 
              placeholder="Buscar por nombre, email o empresa..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-2xl border-none focus:ring-2 focus:ring-[#2C4F1A]/20 outline-none text-sm"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50 text-gray-400 text-xs font-black uppercase tracking-widest">
                <th className="px-6 py-4">Usuario</th>
                <th className="px-6 py-4">Rol / Empresa</th>
                <th className="px-6 py-4">Módulos Activos</th>
                <th className="px-6 py-4">Creado</th>
                <th className="px-6 py-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredUsuarios.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50/80 transition-colors group">
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#2C4F1A]/10 text-[#2C4F1A] flex items-center justify-center font-bold">
                        {user.nombre[0]}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">{user.nombre}</p>
                        <p className="text-xs text-gray-400 font-medium">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex flex-col gap-1">
                      <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full w-fit ${user.rol === 'superadmin' ? 'bg-indigo-50 text-indigo-600' : 'bg-green-50 text-green-600'}`}>
                        {user.rol}
                      </span>
                      <span className="text-xs text-gray-500 font-bold italic">
                        {user.Cliente?.nombre || 'Hunter+ Corp.'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex flex-wrap gap-1">
                      {user.permisos?.slice(0, 3).map(p => (
                        <span key={p} className="text-[10px] bg-gray-100 text-gray-400 px-2 py-0.5 rounded-md font-bold uppercase">{p}</span>
                      ))}
                      {user.permisos?.length > 3 && (
                        <span className="text-[10px] bg-gray-50 text-gray-300 px-1 py-0.5 rounded-md font-bold">+{user.permisos.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2 text-xs text-gray-400 font-bold">
                      <Clock size={14} />
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => handleOpenModal(user)}
                        className="p-2 text-gray-400 hover:text-[#2C4F1A] hover:bg-green-50 rounded-xl transition-all"
                      >
                        <Edit3 size={18} />
                      </button>
                      <button 
                        onClick={() => handleDelete(user.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
            <div className="p-8 pb-4 flex justify-between items-center bg-gray-50/50">
              <div>
                <h2 className="text-2xl font-black text-gray-800 tracking-tight">
                  {editingUser ? 'Editar Usuario' : 'Crear Usuario'}
                </h2>
                <p className="text-sm text-gray-400 font-bold mt-1 uppercase tracking-widest">Configuración de Acceso</p>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-white rounded-full transition-colors text-gray-400"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8 flex-1 overflow-y-auto">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl flex items-center gap-3 text-sm font-bold">
                  <AlertCircle size={18} /> {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Nombre Completo</label>
                  <input 
                    type="text" 
                    required
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    className="w-full px-5 py-4 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-[#2C4F1A]/20 outline-none text-sm font-bold"
                    placeholder="Ej: Juan Pérez"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Email de Acceso</label>
                  <input 
                    type="email" 
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-5 py-4 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-[#2C4F1A]/20 outline-none text-sm font-bold"
                    placeholder="juan@empresa.com"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Contraseña {editingUser && '(vacío para mantener)'}</label>
                  <input 
                    type="password" 
                    required={!editingUser}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-5 py-4 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-[#2C4F1A]/20 outline-none text-sm font-bold"
                    placeholder="••••••••"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Empresa / Cliente</label>
                  <select 
                    value={formData.clienteId}
                    disabled={formData.rol === 'superadmin'}
                    onChange={(e) => setFormData({...formData, clienteId: e.target.value})}
                    className="w-full px-5 py-4 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-[#2C4F1A]/20 outline-none text-sm font-bold appearance-none disabled:opacity-50"
                  >
                    <option value="">Seleccionar empresa...</option>
                    {clientes.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-8">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1 block mb-4">Módulos habilitados</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {availableModules.map(module => (
                    <div 
                      key={module.id}
                      onClick={() => togglePermission(module.id)}
                      className={`p-4 rounded-3xl border-2 transition-all cursor-pointer flex flex-col items-center gap-2 text-center group ${formData.permisos.includes(module.id) 
                        ? 'border-[#2C4F1A] bg-[#2C4F1A]/5 text-[#2C4F1A]' 
                        : 'border-gray-50 bg-gray-50/50 text-gray-300 hover:border-gray-200'}`}
                    >
                      <module.icon className={`w-6 h-6 ${formData.permisos.includes(module.id) ? 'text-[#2C4F1A]' : 'text-gray-300 group-hover:text-gray-400'}`} />
                      <span className="text-[10px] font-black uppercase tracking-tight">{module.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-4 text-gray-400 font-black uppercase text-xs tracking-widest hover:text-gray-600 outline-none"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="flex-[2] bg-[#2C4F1A] text-white py-4 rounded-3xl font-black text-xs uppercase tracking-widest shadow-xl shadow-green-900/20 hover:bg-[#1E3812] transition-all transform active:scale-95"
                >
                  {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
